"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseAdBoardRuleDefinition = exports.AdBoardRuleDefinitionSchema = exports.AD_BOARD_RULE_GROWTH_REACH_FLOOR = exports.AD_BOARD_RULE_DEFAULTS = exports.AD_BOARD_RULE_ITEM_STEPS = exports.AD_BOARD_RULE_MAX_ITEMS = exports.AD_BOARD_RULE_MIN_ITEMS = exports.AD_BOARD_RULE_RANKINGS = exports.parseAdBoardFilterDefinition = exports.emptyAdBoardFilterDefinition = exports.AdBoardFilterDefinitionSchema = exports.AdBoardTrendFilterSchema = exports.AD_BOARD_MAX_STARS = exports.AD_BOARD_MIN_STARS = exports.AD_BOARD_FILTER_STATUSES = exports.AD_BOARD_FILTER_FUNNEL_STAGES = exports.AD_BOARD_FILTER_MEDIA_TYPES = exports.AD_BOARD_FILTER_DEFINITION_VERSION = void 0;
const zod_1 = require("zod");
/**
 * The filter vocabulary shared by the ad researcher, the board viewer, and stored board rules.
 *
 * One schema serves all three so a filter written once works everywhere: as a live researcher
 * query, as a board's viewer control, and as the persisted `ruleDefinition` of a self-populating
 * board. Because rule documents outlive the code that wrote them, the shape is versioned and
 * parsed — never cast — at the QueryService boundary.
 *
 * Literals are declared here rather than imported from the database package (the web app cannot
 * import it); api-side contract tests assert they stay equal to their enums.
 */
/** Current document version. Bump when a change would make an older stored rule parse wrongly. */
exports.AD_BOARD_FILTER_DEFINITION_VERSION = 1;
exports.AD_BOARD_FILTER_MEDIA_TYPES = ['IMAGE', 'VIDEO'];
exports.AD_BOARD_FILTER_FUNNEL_STAGES = ['TOFU', 'MOFU', 'BOFU'];
exports.AD_BOARD_FILTER_STATUSES = ['ACTIVE', 'INACTIVE'];
/** Lowest and highest star rating a filter may ask for. */
exports.AD_BOARD_MIN_STARS = 1;
exports.AD_BOARD_MAX_STARS = 5;
/**
 * Trend-engine references, stored but not yet executed.
 *
 * Only durable identities are storable: a trend uuid survives the weekly refit, and a category is
 * addressed by `facet + name` so it can be re-resolved against whichever run is active when the
 * filter runs. Run-scoped cluster and category ids are deliberately unrepresentable — persisting
 * one would silently point at a deleted row after the next refit.
 */
exports.AdBoardTrendFilterSchema = zod_1.z.object({
    trendUuids: zod_1.z.array(zod_1.z.uuid()).optional(),
    facetCategories: zod_1.z
        .array(zod_1.z.object({
        facet: zod_1.z.enum(['NICHE', 'OFFER']),
        name: zod_1.z.string().min(1),
    }))
        .optional(),
});
exports.AdBoardFilterDefinitionSchema = zod_1.z.object({
    version: zod_1.z.literal(exports.AD_BOARD_FILTER_DEFINITION_VERSION),
    mediaType: zod_1.z.enum(exports.AD_BOARD_FILTER_MEDIA_TYPES).optional(),
    funnelStage: zod_1.z.enum(exports.AD_BOARD_FILTER_FUNNEL_STAGES).optional(),
    status: zod_1.z.enum(exports.AD_BOARD_FILTER_STATUSES).optional(),
    /** Advertiser category keys; validated against the category tree at query time, not here. */
    industries: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    advertiserBrandUuids: zod_1.z.array(zod_1.z.uuid()).optional(),
    daysActiveMin: zod_1.z.number().int().min(0).optional(),
    minStars: zod_1.z.number().int().min(exports.AD_BOARD_MIN_STARS).max(exports.AD_BOARD_MAX_STARS).optional(),
    trend: exports.AdBoardTrendFilterSchema.optional(),
});
/** An empty definition of the current version — the starting point for a new rule or filter set. */
const emptyAdBoardFilterDefinition = () => ({
    version: exports.AD_BOARD_FILTER_DEFINITION_VERSION,
});
exports.emptyAdBoardFilterDefinition = emptyAdBoardFilterDefinition;
/**
 * Parse an unknown value (a stored rule column, a request body) into a filter definition.
 * Returns null instead of throwing so a rule written by a newer version degrades to "no rule"
 * rather than breaking the board that holds it.
 */
const parseAdBoardFilterDefinition = (value) => {
    const result = exports.AdBoardFilterDefinitionSchema.safeParse(value);
    return result.success ? result.data : null;
};
exports.parseAdBoardFilterDefinition = parseAdBoardFilterDefinition;
/**
 * A board rule is a filter definition plus selection: how to rank the pool, how many items the
 * board holds, and whether the rule currently runs. Everything beyond the filter vocabulary is
 * optional so any stored filter document already parses as a rule — same version, no migration.
 */
exports.AD_BOARD_RULE_RANKINGS = ['performance', 'newest', 'growth'];
exports.AD_BOARD_RULE_MIN_ITEMS = 6;
exports.AD_BOARD_RULE_MAX_ITEMS = 100;
/** Cap choices offered by the builder; the schema accepts any value in range. */
exports.AD_BOARD_RULE_ITEM_STEPS = [12, 30, 60, 100];
exports.AD_BOARD_RULE_DEFAULTS = {
    ranking: 'performance',
    maxItems: 30,
    // Off until explicitly switched on: this single constant drives the builder's initial toggle,
    // the parser's resolution of stored documents, the save-path refresh dispatch, and the
    // nightly sweep filter — activation must be a deliberate act, never a side effect of saving.
    enabled: false,
};
/**
 * Growth ranks by relative reach growth, which is pure noise on tiny bases — a fixed floor keeps
 * "9x on 40 reach" out of every growth board. Deliberately not user-tunable.
 */
exports.AD_BOARD_RULE_GROWTH_REACH_FLOOR = 1000;
exports.AdBoardRuleDefinitionSchema = exports.AdBoardFilterDefinitionSchema.extend({
    ranking: zod_1.z.enum(exports.AD_BOARD_RULE_RANKINGS).optional(),
    maxItems: zod_1.z.number().int().min(exports.AD_BOARD_RULE_MIN_ITEMS).max(exports.AD_BOARD_RULE_MAX_ITEMS).optional(),
    enabled: zod_1.z.boolean().optional(),
});
/**
 * Parse an unknown value into an executable rule, resolving selection defaults. Returns null on
 * mismatch for the same reason as `parseAdBoardFilterDefinition`.
 */
const parseAdBoardRuleDefinition = (value) => {
    const result = exports.AdBoardRuleDefinitionSchema.safeParse(value);
    if (!result.success)
        return null;
    return { ...exports.AD_BOARD_RULE_DEFAULTS, ...result.data };
};
exports.parseAdBoardRuleDefinition = parseAdBoardRuleDefinition;
